class ConModalV2 {
    constructor(options = {}) {
        var defaultOptions = {
            iframeUrl: '',
            iframeTitle: 'Embedded content',
            allow: 'gyroscope; fullscreen; accelerometer; camera; microphone; magnetometer; autoplay',
            inline: false,
            targetElement: null,
            bgCover: false,
            aspectRatio: ''
        };
        this.options = Object.assign({}, defaultOptions, options);
        this.isDestroyed = false;

        const iframeUrl = new URL(this.options.iframeUrl || window.location.href, window.location.href);
        if (!['http:', 'https:'].includes(iframeUrl.protocol)) {
            throw new TypeError('[ConModalV2] iframeUrl must use HTTP(S).');
        }
        this.iframeOrigin = iframeUrl.origin;

        this.handleIframeLoad = this.handleIframeLoad.bind(this);
        this.handleMessage = this.handleMessage.bind(this);

        this.ensureLoaderStyles();
        this.createElements();
        this.mount();
        this.bindEvents();

        this.iframe.src = this.options.iframeUrl || 'about:blank';
    }

    createElements() {
        this.iframe = document.createElement('iframe');
        this.iframe.title = this.options.iframeTitle;
        this.iframe.setAttribute('allow', this.options.allow);
        this.iframe.setAttribute('scrolling', 'no');
        this.iframe.style.cssText = 'display:block; width:100%; height:100%; border:none;';

        if (this.options.aspectRatio) {
            this.iframe.style.aspectRatio = this.options.aspectRatio;
        }

        this.loadingIndicator = this.createLoadingIndicator();
    }

    mount() {
        if (this.options.inline) {
            // Support both a CSS selector string and a direct DOM element reference
            if (typeof this.options.targetElement === 'string') {
                this.inlineTarget = document.querySelector(this.options.targetElement);
            } else if (this.options.targetElement instanceof HTMLElement) {
                this.inlineTarget = this.options.targetElement;
            } else {
                this.inlineTarget = document.querySelector('#ff_link[ff_inline="1"]');
            }

            if (!this.inlineTarget) {
                throw new Error('[ConModalV2] Inline target element not found.');
            }

            this.inlineContainer = document.createElement('div');
            this.inlineHeight = '100%';
            this.isInlineExpanded = false;
            this.inlineContainer.className = 'con-modal__inline-target';
            this.inlineContainer.style.cssText = `position:relative; width:100%; height:${this.inlineHeight};`;
            this.inlineContainer.append(this.iframe, this.loadingIndicator);
            this.inlineTarget.appendChild(this.inlineContainer);
            return;
        }

        this.modalContainer = document.createElement('div');
        this.modalContainer.classList.add(`con-modal__container-${Date.now()}`);
        this.modalContainer.style.cssText = 'position:fixed; inset:0; z-index:9999;';

        this.modalContent = document.createElement('div');
        this.modalContent.className = 'con-modal__content';
        this.modalContent.style.cssText = 'position:absolute; inset:0; width:100%; height:100%;';
        this.modalContent.append(this.iframe, this.loadingIndicator);
        this.modalContainer.appendChild(this.modalContent);
        document.body.appendChild(this.modalContainer);
    }

    bindEvents() {
        this.iframe.addEventListener('load', this.handleIframeLoad, { once: true });
        window.addEventListener('message', this.handleMessage);
    }

    handleIframeLoad() {
        var self = this;
        var attempts = 0;
        var maxAttempts = 100; // 10 seconds max

        function checkReady() {
            attempts++;
            try {
                var doc = self.iframe.contentDocument || self.iframe.contentWindow.document;
                if (doc && doc.body && doc.body.scrollHeight > 50) {
                    self.loadingIndicator.remove();
                    return;
                }
            } catch (e) {
                // cross-origin fallback: just remove
                self.loadingIndicator.remove();
                return;
            }

            if (attempts < maxAttempts) {
                setTimeout(checkReady, 100);
            } else {
                self.loadingIndicator.remove();
            }
        }

        checkReady();
    }

    handleMessage(event) {
        if (event.source !== this.iframe.contentWindow || event.origin !== this.iframeOrigin) {
            return;
        }

        const messageData = this.parseMessageData(event.data);
        if (!messageData) return;

        if (messageData.action === 'close_modal[con]!') {
            if (this.options.inline) {
                this.collapseInline();
            } else {
                this.destroy();
            }
            return;
        }

        if (!this.options.inline) return;

        if (messageData.action === 'open_modal[con]!') {
            this.expandInline();
            return;
        }

        if (messageData.action === 'page_info[con]!') {
            this.setInlineHeight(messageData.props && messageData.props.height);
        } else if (messageData.action === 'playerTabsFile') {
            this.setInlineHeight(messageData.body_height);
        } else if (messageData.body_height) {
            this.setInlineHeight(messageData.body_height);
        }
    }

    parseMessageData(messageData) {
        if (typeof messageData === 'string') {
            try {
                messageData = JSON.parse(messageData);
            } catch (e) {
                return null;
            }
        }

        if (!messageData || typeof messageData !== 'object' || Array.isArray(messageData)) {
            return null;
        }

        if (typeof messageData.action === 'string') return messageData;
        if (messageData.body_height !== undefined) return messageData;
        return null;
    }

    expandInline() {
        this.isInlineExpanded = true;
        this.inlineContainer.style.position = 'fixed';
        this.inlineContainer.style.inset = '0';
        this.inlineContainer.style.zIndex = '9999';
        this.inlineContainer.style.width = '100%';
        this.inlineContainer.style.height = '100%';

        if (this.options.bgCover) {
            this.inlineContainer.style.backgroundColor = '#fff';
        }
    }

    collapseInline() {
        this.isInlineExpanded = false;
        this.inlineContainer.style.position = 'relative';
        this.inlineContainer.style.inset = '';
        this.inlineContainer.style.zIndex = '';
        this.inlineContainer.style.height = this.inlineHeight;
        this.inlineContainer.style.backgroundColor = '';
    }

    setInlineHeight(height) {
        const numericHeight = Number(height);
        if (!Number.isFinite(numericHeight) || numericHeight < 0) return;

        this.inlineHeight = `${numericHeight}px`;
        this.inlineTarget.style.height = this.inlineHeight;

        if (!this.isInlineExpanded) {
            this.inlineContainer.style.height = this.inlineHeight;
        }
    }

    destroy() {
        if (this.isDestroyed) return;
        this.isDestroyed = true;

        window.removeEventListener('message', this.handleMessage);
        this.iframe.removeEventListener('load', this.handleIframeLoad);
        this.loadingIndicator.remove();

        if (this.options.inline) {
            if (this.inlineTarget) {
                this.inlineTarget.style.height = '';
            }
            this.inlineContainer.remove();
        } else {
            this.modalContainer.remove();
        }
    }

    ensureLoaderStyles() {
        if (document.getElementById('con-modal-loader-styles')) return;

        const loaderStyles = document.createElement('style');
        loaderStyles.id = 'con-modal-loader-styles';
        loaderStyles.textContent = ` .con-modal__loader { position: absolute; inset: 0; z-index: 9999; display: flex; justify-content: center; align-items: center; pointer-events: none; } .con-modal__loader--modal { background-color: #000000d2; } .con-modal__loader-spinner { display: inline-block; position: relative; width: 80px; height: 80px; } .con-modal__loader-spinner > div { position: absolute; top: 33px; width: 13px; height: 13px; border-radius: 50%; background: #ccc; animation-timing-function: cubic-bezier(0, 1, 1, 0); } .con-modal__loader-spinner > div:nth-child(1) { left: 8px; animation: con-modal-loader-grow 0.6s infinite; } .con-modal__loader-spinner > div:nth-child(2) { left: 8px; animation: con-modal-loader-move 0.6s infinite; } .con-modal__loader-spinner > div:nth-child(3) { left: 32px; animation: con-modal-loader-move 0.6s infinite; } .con-modal__loader-spinner > div:nth-child(4) { left: 56px; animation: con-modal-loader-shrink 0.6s infinite; } @keyframes con-modal-loader-grow { 0% { transform: scale(0); } 100% { transform: scale(1); } } @keyframes con-modal-loader-shrink { 0% { transform: scale(1); } 100% { transform: scale(0); } } @keyframes con-modal-loader-move { 0% { transform: translate(0, 0); } 100% { transform: translate(24px, 0); } } @media (prefers-reduced-motion: reduce) { .con-modal__loader-spinner > div { animation: none; } } `;
        document.head.appendChild(loaderStyles);
    }

    createLoadingIndicator() {
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'con-modal__loader';
        if (!this.options.inline) {
            loadingIndicator.classList.add('con-modal__loader--modal');
        }
        loadingIndicator.setAttribute('role', 'status');
        loadingIndicator.setAttribute('aria-label', 'Loading player');
        loadingIndicator.innerHTML = `<div class="con-modal__loader-spinner"><div></div><div></div><div></div><div></div></div>`;
        return loadingIndicator;
    }
}

export default ConModalV2;



// WEBPACK FOOTER //
// ./src/components/ConModalV2.js